//dependencies
import React, { useEffect, useState } from "react";

import { faChartLine, faInfoCircle, faMemoPad } from "@fortawesome/pro-regular-svg-icons";

import { ButtonIcon, Tooltip } from "@performyard/py-components";

//components
import ExpandableOnPagePanel from "../../../../../components/ExpandableOnPagePanel";
import ActivityStream from "./components/ActivityStream";
import GoalDetails from "./components/GoalDetails";
import GoalProgress from "./components/GoalProgress";

import useSavedPageState from "../../../../../hooks/usePageSavedPageState";
//hooks
import usePageWidthBreakpoint from "../../../../../hooks/usePageWidthBreakpoint";

//config
import Config from "../../config";

const viewMap = {
	activity: <ActivityStream />,
	details: <GoalDetails />,
	progress: <GoalProgress />,
};

const ActivityStreamPanel = () => {
	const { isTablet, isMobile } = usePageWidthBreakpoint();
	const {
		pageState: { isActivityStreamClosed, activityStreamTab = "activity" },
		updatePageState,
	} = useSavedPageState({
		pageName: "goal_progress",
		schema: Config.savedQueryParametersSchema,
	});

	const isActivityStreamOpen = !isActivityStreamClosed;

	// The tooltip portals can lose track of the button when the content is switching, this disables the tooltip
	// briefly on button click, then reenables it after the content is in place
	const [areTooltipsEnabled, setAreTooltipsEnabled] = useState(true);

	useEffect(() => {
		if (!areTooltipsEnabled) {
			const tooltipEnabletimeout = setTimeout(() => {
				setAreTooltipsEnabled(true);
			}, 1000);

			return () => {
				clearTimeout(tooltipEnabletimeout);
			};
		}
	}, [areTooltipsEnabled]);

	return (
		<ExpandableOnPagePanel
			isOpen={isActivityStreamOpen}
			onClose={() => {
				isActivityStreamOpen &&
					updatePageState({ isActivityStreamClosed: true }, { save: !(isTablet || isMobile) });
			}}
			onOpen={() => updatePageState({ isActivityStreamClosed: undefined }, { save: !(isTablet || isMobile) })}
			usePanel={isTablet || isMobile}
			iconList={[
				<Tooltip content="Goal Progress" key="progress" disabled={!areTooltipsEnabled} placement="bottom">
					<ButtonIcon
						variant="outline"
						color={"neutral"}
						size={32}
						icon={faChartLine}
						data-testid="progress-stream-button"
						onClick={() => {
							setAreTooltipsEnabled(false);
							updatePageState({ isActivityStreamClosed: undefined, activityStreamTab: "progress" });
						}}
						dangerouslySetClassName={
							isActivityStreamOpen && activityStreamTab === "progress"
								? "border-neutral-7 bg-neutral-7 text-neutral-0 hover:bg-neutral-7"
								: ""
						}
					/>
				</Tooltip>,
				<Tooltip content="Activity Stream" key="activity" disabled={!areTooltipsEnabled} placement="bottom">
					<ButtonIcon
						variant="outline"
						color={"neutral"}
						size={32}
						icon={faMemoPad}
						data-testid="activity-stream-button"
						onClick={() => {
							setAreTooltipsEnabled(false);
							updatePageState({ isActivityStreamClosed: undefined, activityStreamTab: "activity" });
						}}
						dangerouslySetClassName={
							isActivityStreamOpen && activityStreamTab === "activity"
								? "border-neutral-7 bg-neutral-7 text-neutral-0 hover:bg-neutral-7"
								: ""
						}
					/>
				</Tooltip>,
				<Tooltip content="Goal Details" key="details" disabled={!areTooltipsEnabled} placement="bottom">
					<ButtonIcon
						variant="outline"
						color={"neutral"}
						size={32}
						icon={faInfoCircle}
						data-testid="details-stream-button"
						onClick={() => {
							setAreTooltipsEnabled(false);
							updatePageState({ isActivityStreamClosed: undefined, activityStreamTab: "details" });
						}}
						dangerouslySetClassName={
							isActivityStreamOpen && activityStreamTab === "details"
								? "border-neutral-7 bg-neutral-7 text-neutral-0 hover:bg-neutral-7"
								: ""
						}
					/>
				</Tooltip>,
			]}
		>
			{viewMap[activityStreamTab]}
		</ExpandableOnPagePanel>
	);
};

export default ActivityStreamPanel;
